import { Resolution, Margin } from 'react-to-pdf';
import { tInputModes, tSelectLabelValue } from '../types';

// ─── DEPRECATED: Use Permission and Role from utils/permissions.ts instead ───
// Kept temporarily for backward compatibility during migration.
import {
    Permission,
    Role,
    ALL_PERMISSION_OPTIONS,
    DEFAULT_USER_PERMISSION_OPTIONS,
    DEFAULT_ADMIN_PERMISSION_OPTIONS,
} from './permissions';

/** @deprecated Use Permission from utils/permissions.ts */
export const PERMISSION_VALUES = Permission;
/** @deprecated Use Role from utils/permissions.ts */
export const ROLES = Role;

/** @deprecated Use ALL_PERMISSION_OPTIONS from utils/permissions.ts */
export const ALL_USER_PERMISSIONS = ALL_PERMISSION_OPTIONS;
/** @deprecated Use DEFAULT_USER_PERMISSION_OPTIONS from utils/permissions.ts */
export const DEFAULT_USER_PERMISSIONS = DEFAULT_USER_PERMISSION_OPTIONS;
/** @deprecated Use DEFAULT_ADMIN_PERMISSION_OPTIONS from utils/permissions.ts */
export const DEFAULT_ADMIN_PERMISSIONS = DEFAULT_ADMIN_PERMISSION_OPTIONS;
/** @deprecated No longer needed — super admin bypasses permission checks */
export const SUPER_ADMIN_USER_PERMISSIONS = ALL_PERMISSION_OPTIONS;
/** @deprecated No longer needed */
export const DEFAULT_SUPER_ADMIN_PERMISSIONS = DEFAULT_ADMIN_PERMISSION_OPTIONS;

/**
 * Sentinel companyId used for platform-level training templates built by
 * business admins. Topics/plans stored under this ID are not tied to any
 * real company and can be shared (copied) to any company at will.
 */
export const LIBRARY_TEMPLATE_COMPANY_ID = 'library-template';

export const TIRE_CATEGORIES = {
    CONSTRUCTION: 'Construction',
    COMMERCIAL: 'Commercial - Truck - Trailer - Bus',
    FARM: 'Farm - Ag',
    AUTO: 'Auto - LT - SUV - Trailer',
    MOTORCYCLE: 'Motorcycle',
    AVIATION: 'Aviation',
    MILITARY: 'Military - Land',
    ATVS: 'ATV - UTV - Lawn & Garden',
};

/** Mobile drawer breakpoint. Matches the old Cloudscape AppLayout `xs` cutoff. */
export const CLOUDSCAPE_MOBILE_BREAKPOINT = 688;

export const SUBCRIPTION_TYPE_AND_DESCRIPTION = [
    {
        value: 'free-demo',
        label: 'Free Demo',
        description: 'This is the basic free trial with limited access to the application.',
    },
    {
        value: 'paid-limited-access',
        label: 'Limited Subscription',
        description: 'Paid subscription with limits to users and equipment',
    },
    { value: 'paid-full-access', label: 'Full Subscription', description: 'This is the full access subscription.' },
    { value: 'paused', label: 'Pause', description: 'If an account is behind on payment pause until balance is paid.' },
];

export const RESET_EQUIPMENT_UNIT = {
    id: null,
    categoryId: '',
    name: '',
    description: null as string | null,
    image: null as string | null,
    manufacturerName: null as string | null,
    manufactureDate: null as string | null,
    modelNumber: null as string | null,
    aquisitionDate: null as string | null,
    aquisitionCost: null as number | null,
    inputDate: null as string | null,
    inputByPerson: null as string | null,
    serialNumber: null as string | null,
    licenseNumber: null as string | null,
    currentUMCounterValue: null as number | null,
    machineUMCounterAtAquisition: null as number | null,
    lastInspectionDate: null as string | null,
    annualProjectedUsage: null as number | null,
    downReason: null as string | null,
    locationId: null as string | null,
    dailyServiceLogFormId: null as string | null,
    extraUnitInfo: null as string | null,
    equipmentStatus: true,
    putInServiceDate: null as string | null,
    removedFromServiceDate: null as string | null,
    removedFromServiceReason: null as string | null,
    dispositionReason: null as string | null,
    dispositionDate: null as string | null,
    unitOfMeasure: null as string | null,
    replacedUMCounterStatus: false,
    replacedUMCounterValue: null as number | null,
};

export const RESET_NEW_LOCATION = { name: '', description: '', regionalDivisionId: '', companyId: '' };

export const NETWORK_DOMAIN = `${window.location.protocol}//${window.location.host}`;

export const MAX_NUMBER_OF_PHOTOS_PER_PROMPT = 3;
export const WEBCAM_DIMENSIONS = {
    height: 500,
    width: 330,
};

export const SYSTEM_MESSAGE_STRINGS = {
    LOADING: 'Loading...',
};

/** Auto-close duration for Home Daily Brief launch overlay (ms). */
export const DAILY_BRIEFING_AUTO_DURATION_MS = 25_000;

export const LOCAL_STORAGE_KEYS = {
    CAMERA: 'camera',
    EQUIPMENT_NAME: 'equipmentName',
    SELECTED_UNIT: 'selectedUnit',
    LOCATION: 'location',
    REPORT_TO_SEND: 'reportToSend',
    REPORT_QUEUE: 'reportQueue',
    TRAINING_QUEUE: 'trainingQueue',
    REPORT: 'report',
    SAFETY_REPORT: 'safetyReport',
    START_OF_DAY_REPORT: 'startOfDayReport',
    LAST_REPORT: 'lastReport',
    INSPECT: 'inspect',
    PRINT_USER: 'printUser',
    PRINT_LESSONS: 'printLessons',
    REPORT_ACTIVE_TAB: 'reportSafetyTab',
    REPORT_SERVICE_ACTIVE_TAB: 'inspectionReports',
    REPORT_SAFETY_SELECTED_EMPLOYEE: 'reportSafetySelectedEmployee',
    REPORT_SAFETY_DATE_RANGE: 'reportSafetyDateRange',
    REPORT_INSPECTION_DATE_RANGE: 'reportInspectionDateRange',
    REPORT_FILTERED_BY_EQUIPMENT: 'reportFilteredByEquipment',
    REPORT_FILTERED_BY_UNIT: 'reportFilteredByUnit',
    REPORT_FILTERED_BY_REGION: 'reportFilteredByRegion',
    REPORT_FILTERED_BY_LOCATION: 'reportFilteredByLocation',
    ISSUE_FILTERED_BY_EQUIPMENT: 'issueFilteredByEquipment',
    ISSUE_FILTERED_BY_UNIT: 'issueFilteredByUnit',
    ISSUE_FILTERED_BY_REGION: 'issueFilteredByRegion',
    ISSUE_FILTERED_BY_LOCATION: 'issueFilteredByLocation',
    SIGNATURE_PATH: 'signaturePath',
    SAFETY_SIGNATURE_PATH: 'safetySignaturePath',
    UNIT_ID: 'unitId',
    TIME_INSPECTION_STARTED: 'timeInspectionStarted',
    HOME_EQUIPMENT_LIST_EXPANDED: 'homeEquipmentListExpanded',
    EQUIPMENT_EXPANDED_CATEGORIES: 'equipmentExpandedCategories',
    /** Resources → Service Items: expanded category accordion ids (suffix companyId). */
    SERVICE_ITEMS_EXPANDED_CATEGORIES: 'serviceItemsExpandedCategories',
    /** Resources → Service Procedures: expanded category accordion ids (suffix companyId). */
    SERVICE_PROCEDURES_EXPANDED_CATEGORIES: 'serviceProceduresExpandedCategories',
    /** Resources → Service Procedures: expanded procedure accordion ids (suffix companyId). */
    SERVICE_PROCEDURES_EXPANDED_ITEMS: 'serviceProceduresExpandedItems',
    /** User Skillsets → Classifications: expanded classification accordion ids (suffix companyId). */
    SKILLSETS_EXPANDED_CLASSIFICATIONS: 'skillsetsExpandedClassifications',
    /** Daily Briefing Topic Library rows-per-page preference */
    DAILY_BRIEFING_TOPIC_PAGE_SIZE: 'dailyBriefingTopicPageSize',
    /** Home auto-launch: last local date key the Daily Brief overlay was shown (suffix companyId:userId). */
    DAILY_BRIEFING_AUTO_SHOWN: 'dailyBriefingAutoShown',
    /** Prototype: per-user home form favorites (localStorage until UserFormFavorite lands). */
    HOME_FAVORITE_FORM_IDS: 'homeFavoriteFormIds',
    /** sessionStorage: last successful /report-sent payload for refresh-safe success UI */
    REPORT_SENT_SUMMARY: 'reportSentSummary',
    /** sessionStorage: FormBuilder in-progress draft (suffix companyId + new|edit|template) */
    FORM_BUILDER_DRAFT: 'formBuilderDraft',
    /** sessionStorage: in-progress inspection wizard (prompts + step) for refresh/offline resume */
    INSPECTION_DRAFT: 'inspectionDraft',
};

export const REPORT_TYPES = {
    INSPECTION: 'InspectionReport',
    DAILY_SERVICE: 'DailyServiceReport',
    SHIFT: 'ShiftReport',
    DOT_INSPECTION: 'DOTInspectionReport',
    USER_SKILL: 'UserSkillReport',
} as const;

export const FORM_TYPES = {
    INSPECTION: 'inspection',
    DAILY_SERVICE: 'dailyService',
    SHIFT_REPORT: 'shiftReport',
    DOT_INSPECTION: 'dotInspection',
    USER_SKILL: 'userSkill',
    PM_SERVICE: 'pmService',
} as const;

/** Public `/forms/new?type=` slugs. Stored form type remains `FORM_TYPES` (camelCase). */
export const FORM_TYPE_QUERY = {
    [FORM_TYPES.INSPECTION]: 'inspection',
    [FORM_TYPES.DAILY_SERVICE]: 'daily-service',
    [FORM_TYPES.SHIFT_REPORT]: 'shift-report',
} as const;

export function formTypeFromQueryParam(type: string | null | undefined): string {
    if (!type) return FORM_TYPES.INSPECTION;
    if (type === FORM_TYPE_QUERY[FORM_TYPES.DAILY_SERVICE] || type === FORM_TYPES.DAILY_SERVICE) {
        return FORM_TYPES.DAILY_SERVICE;
    }
    if (type === FORM_TYPE_QUERY[FORM_TYPES.SHIFT_REPORT] || type === FORM_TYPES.SHIFT_REPORT) {
        return FORM_TYPES.SHIFT_REPORT;
    }
    return FORM_TYPES.INSPECTION;
}

export function formTypeToQueryParam(formType: string): string {
    if (formType === FORM_TYPES.DAILY_SERVICE) return FORM_TYPE_QUERY[FORM_TYPES.DAILY_SERVICE];
    if (formType === FORM_TYPES.SHIFT_REPORT) return FORM_TYPE_QUERY[FORM_TYPES.SHIFT_REPORT];
    return FORM_TYPE_QUERY[FORM_TYPES.INSPECTION];
}

export function formsListPath(formType: string): string {
    return `/forms?type=${formTypeToQueryParam(formType)}`;
}

export const S3_IMAGE_PATH = {
    fullEquipmentImagePath: (companyId: string, fileName: string) => `public/equipmentImage/${companyId}/${fileName}`,
    fullInspectionReportImagePath: (companyId: string, fileName: string) =>
        `public/inspectionReportImage/${companyId}/${fileName}`,
    signatureImagePath: (companyId: string, fileName: string) => `public/signatureImage/${companyId}/${fileName}`,
    trainingDailySignaturePath: (companyId: string, fileName: string) =>
        `public/trainingDailySignature/${companyId}/${fileName}`,
    companyLogoImagePath: (ownerId: string, fileName: string) => `public/companyLogo/${ownerId}/${fileName}`,
    userPhotoPath: (companyId: string, userId: string, fileName: string) =>
        `public/userPhoto/${companyId}/${userId}/${fileName}`,
};

export const S3_RESOURCE_LIBRARY_PATH = {
    documentPath: (companyOwnerId: string, fileName: string) => `public/document/${companyOwnerId}/${fileName}`,
    videoPath: (companyOwnerId: string, fileName: string) => `public/video/${companyOwnerId}/${fileName}`,
};

export const LOCATION_TYPES = {
    gps: 'Device GPS',
    lastKnownPosition: 'Last Known Position',
    unknown: 'Unknown',
    ip: 'IP Address',
};

export const DEFAULT_REPORT_LABELS = {
    currentMachineUM: 'Current Machine UM Counter Value',
    currentMachineHours: 'Current Machine UM Counter Value', // deprecated alias — use currentMachineUM
    lastServiceUM: 'Last Service UM Counter Value',
    lastServiceHours: 'Last Service UM Counter Value', // deprecated alias — use lastServiceUM
    UMSinceLastService: 'UM Since Last Service',
    timeSinceLastService: 'Time Since Last Service',
    dailyServiceLogForm: 'Daily Service Log Form',
    locationAndRegion: 'Location and (Region)',
};

export const DEFAULT_FORM_INPUT_TYPES = {
    standAloneQuestion: 'standAloneQuestion',
    section: 'section',
    yesNo: 'yesNo',
    checkbox: 'checkbox',
    dropdown: 'dropdown',
    sectionQuestion: 'sectionQuestion',
    fourResponse: 'fourResponse',
};

// Operator response type patterns for daily service form items
export const OPERATOR_RESPONSE_TYPES = {
    ADDED_NOT_ADDED: 'addedNotAdded',
    OK_CLEANED_REPLACED_NA: 'okCleanedReplacedNA',
    YES_NO: 'yesNo',
    FOUR_BUTTON: 'fourResponse',
    OK_REPLACED_NA: 'okReplacedNA',
    QUANTITY_ONLY: 'quantityOnly',
    ACKNOWLEDGEMENT: 'acknowledgement',
    NEW_ISSUES: 'newIssues',
} as const;

export const OPERATOR_RESPONSE_TYPE_OPTIONS = [
    { value: OPERATOR_RESPONSE_TYPES.YES_NO, label: 'Yes / No' },
    { value: OPERATOR_RESPONSE_TYPES.ADDED_NOT_ADDED, label: 'Added / Not Added' },
    { value: OPERATOR_RESPONSE_TYPES.OK_CLEANED_REPLACED_NA, label: 'OK / Cleaned / Replaced / N/A' },
    { value: OPERATOR_RESPONSE_TYPES.OK_REPLACED_NA, label: 'OK / Replaced / N/A' },
    { value: OPERATOR_RESPONSE_TYPES.FOUR_BUTTON, label: '4-Button (icons)' },
    { value: OPERATOR_RESPONSE_TYPES.QUANTITY_ONLY, label: 'Quantity Only (number field)' },
] as const;

/** Daily Service item rows record a quantity. Other response patterns stay available on saved questions. */
export const DAILY_SERVICE_OPERATOR_RESPONSE_TYPE_OPTIONS = OPERATOR_RESPONSE_TYPE_OPTIONS.filter(
    (option) => option.value === OPERATOR_RESPONSE_TYPES.QUANTITY_ONLY,
);

/** Question / response types for inspection (and shift) forms — not daily-service patterns. */
export const INSPECTION_QUESTION_TYPE_OPTIONS = [
    {
        value: OPERATOR_RESPONSE_TYPES.FOUR_BUTTON,
        label: '4 Button (Good / Warning / Fail / N/A)',
        group: 'Response Patterns' as const,
    },
    { value: OPERATOR_RESPONSE_TYPES.YES_NO, label: 'Yes / No', group: 'Response Patterns' as const },
    {
        value: DEFAULT_FORM_INPUT_TYPES.checkbox,
        label: 'Checkbox (multi-select)',
        group: 'Custom Input' as const,
    },
    {
        value: DEFAULT_FORM_INPUT_TYPES.dropdown,
        label: 'Dropdown (single-select)',
        group: 'Custom Input' as const,
    },
    {
        value: DEFAULT_FORM_INPUT_TYPES.sectionQuestion,
        label: 'Question Field (text input)',
        group: 'Custom Input' as const,
    },
] as const;

// Categories for daily service form items
export const FORM_ITEM_CATEGORIES = {
    NORMAL_CONSUMABLE: 'normalConsumable',
    IMPLEMENT_SERVICE: 'implementService',
    ADVERSE_CONDITIONS: 'adverseConditions',
    PREVENTATIVE_PM: 'preventativePM',
    PROCEDURE: 'procedure',
    NEW_ISSUES: 'newIssues',
} as const;

export const DAILY_SERVICE_ITEM_CATEGORY_VALUES = [
    FORM_ITEM_CATEGORIES.NORMAL_CONSUMABLE,
    FORM_ITEM_CATEGORIES.IMPLEMENT_SERVICE,
] as const;

export const FORM_ITEM_CATEGORY_OPTIONS = [
    {
        value: FORM_ITEM_CATEGORIES.NORMAL_CONSUMABLE,
        label: 'Normal Consumable',
        shortLabel: 'Normal',
        description: 'Expected daily use – fuel, DEF, grease.',
        statusTone: 'info',
        color: '#1e40af',
    },
    {
        value: FORM_ITEM_CATEGORIES.IMPLEMENT_SERVICE,
        label: 'Unplanned Service',
        shortLabel: 'Unplanned',
        description: 'Oil, coolant – indicates potential issue.',
        statusTone: 'warning',
        color: '#92400e',
    },
    {
        value: FORM_ITEM_CATEGORIES.ADVERSE_CONDITIONS,
        label: 'Adverse Conditions',
        shortLabel: 'Adverse',
        description: 'Filter replaced due to dust, water in fuel.',
        statusTone: 'danger',
        color: '#991b1b',
    },
    {
        value: FORM_ITEM_CATEGORIES.PREVENTATIVE_PM,
        label: 'Preventative PM',
        shortLabel: 'Preventative PM',
        description: 'Scheduled service procedure.',
        statusTone: 'success',
        color: '#065f46',
    },
] as const;

/** Categories offered when adding a Daily Service item. Saved items may still use the others. */
export const DAILY_SERVICE_ITEM_CATEGORY_OPTIONS = FORM_ITEM_CATEGORY_OPTIONS.filter((option) =>
    (DAILY_SERVICE_ITEM_CATEGORY_VALUES as readonly string[]).includes(option.value),
);

export const UNITS_OF_MEASURE = [
    { label: 'Hours', value: 'hours' },
    { label: 'Miles', value: 'miles' },
    { label: 'Kilometers', value: 'kilometers' },
    { label: 'Cycles', value: 'cycles' },
    { label: 'Gallons', value: 'gallons' },
    { label: 'Liters', value: 'liters' },
    { label: 'Days', value: 'days' },
    { label: 'Counts', value: 'counts' },
    { label: 'Time', value: 'time' },
    { label: 'PSI', value: 'psi' },
] as const;

// UM counter label options for form-level setting (subset of UNITS_OF_MEASURE)
export const UM_COUNTER_LABEL_OPTIONS = UNITS_OF_MEASURE.filter((unit) =>
    ['hours', 'miles', 'kilometers', 'cycles', 'gallons', 'liters'].includes(unit.value),
);

export enum SEARCH_PARAMS {
    SETTINGS = 'settings',
    EMAIL = 'email',
    ISSUE_ID = 'issueId',
    EMPTY = '',
}

/** Elements matching this are omitted from html2canvas PDF captures. */
export const PDF_EXPORT_EXCLUDE_SELECTOR = '.report-export-wrapper, [data-pdf-exclude]';

export const isPdfExportExcluded = (element: Element): boolean =>
    element.matches(PDF_EXPORT_EXCLUDE_SELECTOR) || element.closest(PDF_EXPORT_EXCLUDE_SELECTOR) !== null;

export const PDF_OPTIONS = {
    // default is `save`
    method: 'save',
    // default is Resolution.MEDIUM = 3, which should be enough, higher values
    // increases the image quality but also the size of the PDF, so be careful
    // using values higher than 10 when having multiple pages generated, it
    // might cause the page to crash or hang.
    resolution: Resolution.MEDIUM,
    page: {
        // margin is in MM, default is Margin.NONE = 0
        margin: Margin.SMALL,
        // default is 'A4'
        format: 'letter',
        // default is 'portrait'
        orientation: 'portrait',
    },
    canvas: {
        // default is 'image/jpeg' for better size performance
        mimeType: 'image/jpeg',
        qualityRatio: 1,
    },
    // Customize any value passed to the jsPDF instance and html2canvas
    // function. You probably will not need this and things can break,
    // so use with caution.
    overrides: {
        // see https://artskydj.github.io/jsPDF/docs/jsPDF.html for more options
        pdf: {
            compress: true,
        },
        // see https://html2canvas.hertzen.com/configuration for more options
        canvas: {
            useCORS: true,
            ignoreElements: isPdfExportExcluded,
        },
    },
};

export const KEYS_START_OF_DAY_REPORT = {
    EQUIPMENT_ID: 'Start-of-Day',
    EQUIPMENT_NAME: 'Start of Day Report',
};

export const REPORT_SENDING_STATE = {
    CHECKING_NETWORK: 'Checking Network',
    REPORT_SENT: 'Report Sent',
    REPORT_SENDING: 'Report Sending',
    REPORT_ERROR: 'Error - Report not sent: ',
    OFFLINE: 'Offline - Report is stored and will be sent when the connection is restored.',
};

export const TRAINING_SENDING_STATE = {
    CHECKING_NETWORK: 'Checking Network',
    SENDING: 'Sending Training Data',
    SENT: 'Training Data Sent',
    ERROR: 'Error - Training data not sent: ',
    OFFLINE: 'Offline - Training data is stored and will be sent when the connection is restored.',
};

export const ITEM_STATUS_CODES = {
    inop: 'inop',
    warn: 'warn',
    ok: 'ok',
    na: 'na',
    under_review: 'under_review',
    status_unchanged: 'status_unchanged',
};

export enum ITEM_STATUS_CONTENT {
    inop = 'Out Of Service',
    warn = 'Warning',
    ok = 'OK',
    resolved = 'Resolved',
    no_change = 'No Change',
    under_review = 'Under Review',
}

export const DROPDOWN_CONFIG = {
    statusBasedFields: {
        [ITEM_STATUS_CODES.inop]: {
            label: 'Out Of Service Details',
            fields: [
                { type: 'textarea', label: 'Description of Issue', required: true },
                { type: 'text', label: 'Estimated Repair Time', required: false },
                { type: 'text', label: 'Parts Needed', required: false },
            ],
        },
        [ITEM_STATUS_CODES.warn]: {
            label: 'Warning Details',
            fields: [
                { type: 'textarea', label: 'Warning Description', required: true },
                { type: 'text', label: 'Recommended Action', required: false },
            ],
        },
        [ITEM_STATUS_CODES.ok]: {
            label: 'OK Status',
            fields: [{ type: 'text', label: 'Additional Notes', required: false }],
        },
        [ITEM_STATUS_CODES.na]: {
            label: 'Not Applicable',
            fields: [{ type: 'text', label: 'Reason N/A', required: false }],
        },
    },
};

export const ALL_EQUIPMENT: tSelectLabelValue = {
    label: 'All Equipment',
    value: 'all',
};

export const INPUT_MODES: tInputModes = {
    TEXT: 'text',
    NUMBER: 'decimal',
    TEXTAREA: 'textarea',
};

export const SITE_ADMIN_NAV = [
    {
        text: 'Customer Management',
        description: 'Dashboard to Manage All Subscribed or Free Tier Company Owners',
        href: '/site-admin',
        icon: 'logo-images/orange-zorva-vector.png',
    },
    {
        text: 'Resource Library',
        description: 'Manage site wide resources',
        href: '/resource-library',
        icon: 'library-svgrepo-com.svg',
    },
];

export const NAV_ELEMENTS = [
    {
        text: 'Home',
        description: '',
        href: '/',
        icon: 'home-line-svgrepo-com.svg',
    },

    {
        text: 'Regions',
        permissions: Permission.MANAGE_REGIONS,
        description: 'Click to manage locations within the regions (add, edit, or delete)',
        href: '/regions',
        icon: 'map-location-svgrepo-com.svg',
    },
    {
        text: 'Equipment',
        permissions: Permission.MANAGE_EQUIPMENT,
        description: 'Click to manage, add, delete equipment attributed to the company',
        href: '/equipment',
        icon: 'sideNav-icons/construction-dump-dumper-svgrepo-com.svg',
    },
    {
        text: 'Forms',
        permissions: Permission.MANAGE_FORMS,
        href: '/forms',
        description: 'Manage inspection forms',
        icon: 'sideNav-icons/checklist-svgrepo-com.svg',
    },
    {
        text: 'Reports & Analytics',
        permissions: Permission.VIEW_REPORTS,
        href: '/reports',
        description: 'View inspection reports',
        icon: 'sideNav-icons/documents-svgrepo.svg',
    },
    {
        text: 'Issues',
        permissions: Permission.VIEW_DISCREPANCIES,
        href: '/issues',
        description: 'View and classify all current issues',
        icon: 'sideNav-icons/warning-triangle-svgrepo-com.svg',
    },
    {
        text: 'Users',
        permissions: Permission.VIEW_USERS,
        description: 'Click to manage users within the company (add, edit, or delete)',
        href: '/users',
        icon: 'sideNav-icons/worker-contractor-svgrepo-com.svg',
    },
    {
        text: 'Resources',
        permissions: Permission.VIEW_RESOURCES,
        description: 'Click to view company resource library',
        href: '/resources',
        icon: 'sideNav-icons/documents-document-svgrepo-com.svg',
    },
    {
        text: 'Safety & Training',
        permissions: Permission.MANAGE_SAFETY_AND_TRAINING,
        companyEnrollment: 'trainingModuleEnrollment',
        description: 'Manage safety training plans, topics, schedules, and compliance',
        href: '/safety-training',
        icon: 'sideNav-icons/calendar-add-event-svgrepo-com.svg',
    },
    {
        text: 'PM Module',
        permissions: Permission.VIEW_PM,
        companyEnrollment: 'pmModuleEnrollment',
        description: 'Click to view PM for company equipment',
        href: '/pm-management',
        icon: 'sideNav-icons/pm-icon.svg',
    },
    {
        text: 'Cost Tracking Module',
        permissions: Permission.MANAGE_COST_TRACKING,
        companyEnrollment: 'costTrackingModuleEnrollment',
        description: 'Click to view and manage company costs',
        href: '/cost-tracking',
        icon: 'sideNav-icons/cost-module-icon.svg',
    },
    {
        text: 'Tire Maintenance Module',
        permissions: Permission.MANAGE_TIRE_MAINTENANCE,
        companyEnrollment: 'tiresModuleEnrollment',
        description: 'Click to view and manage tire maintenance for company equipment',
        href: '/tire-maintenance',
        icon: 'sideNav-icons/tire-maintenance-icon.svg',
    },
    // {
    //     text: 'User Skillset Module',
    //     permissions: Permission.MANAGE_SKILL_TRACKING,
    //     companyEnrollment: 'skillTrackingModuleEnrollment',
    //     description: 'Click to view and manage company user skill tracking',
    //     href: '/skill-tracking',
    //     icon: 'sideNav-icons/calendar-add-event-svgrepo-com.svg'
    // },
];
// Support section
export const SUPPORT_NAV = [
    {
        text: 'Inbox',
        href: '/inbox',
        icon: 'sideNav-icons/inbox-icon.svg',
    },
    {
        text: 'Get help',
        href: '/help',
        icon: 'sideNav-icons/get-help-icon.svg',
    },
];

// System section
export const SYSTEM_NAV = [
    {
        text: 'Settings',
        permissions: Permission.MANAGE_SETTINGS,
        href: '/settings',
        icon: 'sideNav-icons/settings-icon.svg',
    },
    {
        text: 'Company Profile',
        permissions: Permission.MANAGE_COMPANY,
        description: 'View your company profile, subscription, and billing',
        href: '/company',
        icon: 'sideNav-icons/construction-dump-dumper-svgrepo-com.svg',
    },
    {
        text: 'Logout',
        href: '/logout',
        icon: 'sideNav-icons/logout-svgrepo-com.svg',
    },
];

export const MAX_NUMBER_USERS_FREE_DEMO = 4;
export const MAX_NUMBER_EQUIPMENT_FREE_DEMO = 4;
export const MAX_NUMBER_FORMS_FREE_DEMO = 4;
export const MAX_NUMBER_COMPANY_FREE_DEMO = 1;

export const SPLASH_IMAGES = {
    LOGO: {
        src: '/logo-images/black-zorva-white-background.png',
        alt: 'Zorva Logo',
    },
    HERO_MAIN: {
        src: '/splashpage-images/cylinder-splash-home.jpg',
        alt: 'Construction equipment',
    },
    FORMS: {
        src: '/splashpage-images/dozer-splash-forms.jpg',
        alt: 'Equipment forms',
    },
    INSPECT: {
        src: '/splashpage-images/QR-splash-inspect.jpg',
        alt: 'Quarry operations',
    },
    REPORTS: {
        src: '/splashpage-images/QR-splash-inspect.jpg',
        alt: 'image of Quarry operations',
    },
    TESTIMONIALS: {
        src: '/splashpage-images/dozer-splash-forms.jpg',
        alt: 'Equipment forms',
    },
    IF_I_HAD_KNOWN: {
        src: '/splashpage-images/dozer-splash-forms.jpg',
        alt: 'Equipment forms',
    },
    FOOTER_LOGO: {
        src: '/logo-images/white-zorva-navy-background.png',
        alt: 'Zorva Logo',
    },
};
